import styled from "styled-components"
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ThreeDots } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { TextField } from "@mui/material";
import Button from "@mui/material/Button";
import { RotatingLines } from "react-loader-spinner";
import { useLoginJWTAuth, useFetchEntities } from "../../reactQueryHooks";
const logo = require("./gg_logo_only_text2.png");

//------------------------------------------------------------------------

const formSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const Container = styled.div`
  flex: 1;
  display: flex;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const LoginFormContainer = styled(motion.div)`
  display: ${({ shown }) => (shown === "shown" ? "flex" : "none")};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 200px;
  background-color: white;
  padding: 15px;
  border-radius: 5px;
  box-shadow: 0px 0px 15px 5px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.img`
  width: 80%;
  object-fit: contain;
  margin-bottom: 10px;
`;

const CopyRight = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 12px;
  color: grey;
`;

const InfoButton = styled.button`
  position: absolute;
  bottom: 15px;
  left: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #fafcfc;
  color: #6d6d6d;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f0f0;
    color: ${({ theme }) => theme.colors.blue};
  }
`;

const StyledDialog = styled.dialog`
  border: none;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;

  &::backdrop {
    background: rgba(0, 0, 0, 0.5);
  }
`;

const DialogContent = styled.div`
  font-size: 13px;
  color: #393939;
  line-height: 1.8;

  div {
    margin-bottom: 8px;
  }

  strong {
    font-weight: 600;
  }

  a {
    color: ${({ theme }) => theme.colors.blue};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #393939;
  }

  button {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #6d6d6d;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #393939;
    }
  }
`;

//------------------------------------------------------------------------

const LoginContent = () => {
  const { loginJWTAuth } = useLoginJWTAuth();
  const [loginOk, setLoginOk] = useState(false);
  const { isLoading, isFetched } = useFetchEntities();
  const navigate = useNavigate();
  const dialogRef = useRef(null);

  const openDialog = () => {
    dialogRef.current?.showModal();
  };

  const closeDialog = () => {
    dialogRef.current?.close();
  };

  useEffect(() => {
    if (loginOk && isFetched) {
      navigate("/home");
    }
  }, [isFetched, navigate, loginOk]);

  return (
    <Container>
      <ContentContainer>
        <Formik
          initialValues={{
            username: "",
            password: "",
          }}
          validationSchema={formSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setSubmitting(true);
              await loginJWTAuth(values);
              setSubmitting(false);
              setLoginOk(true);
            } catch (error) {
              console.log("error", error);
              alert(error?.response?.data);
              setSubmitting(false);
              setLoginOk(false);
            }
          }}
        >
          {({ values, errors, handleChange, handleSubmit, isSubmitting }) => {
            return (
              <LoginFormContainer
                shown={!loginOk ? "shown" : "hidden"}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const itHasErrors = Object.keys(errors).length > 0;
                    if (itHasErrors) {
                      console.log("errors", errors);
                      alert(Object.values(errors).join(", "), "");
                    } else {
                      handleSubmit();
                    }
                  }
                }}
              >
                <Logo src={logo} />

                <TextField
                  variant="filled"
                  label="Username"
                  name="username"
                  onChange={handleChange}
                  value={values.username}
                  size="small"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    htmlInput: {
                      style: { fontSize: 13 },
                    },
                  }}
                  error={errors.username ? true : false}
                />
                <TextField
                  variant="filled"
                  label="Password"
                  type="password"
                  name="password"
                  onChange={handleChange}
                  value={values.password}
                  size="small"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    htmlInput: {
                      style: { fontSize: 13 },
                    },
                  }}
                  error={errors.password ? true : false}
                />
                <Button
                  type="submit"
                  onClick={() => {
                    const itHasErrors = Object.keys(errors).length > 0;
                    if (itHasErrors) {
                      console.log("errors", errors);
                      alert(Object.values(errors).join(", "), "");
                    } else {
                      handleSubmit();
                    }
                  }}
                  variant="outlined"
                  fullWidth
                  size="large"
                  style={{
                    border: "1px solid #f3f3f3",
                    height: "45px",
                  }}
                >
                  {!isSubmitting && (
                    <i
                      style={{
                        color: "darkgreen",
                      }}
                      className="fa-solid fa-check"
                    />
                  )}
                  {isSubmitting && (
                    <RotatingLines
                      width="17"
                      strokeColor="grey"
                      //speed
                      strokeWidth={4}
                    />
                  )}
                </Button>
              </LoginFormContainer>
            );
          }}
        </Formik>

        {loginOk && isLoading && (
          <ThreeDots
            height="50"
            width="50"
            radius="9"
            color="grey"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        )}
        <CopyRight>
          &copy; This software is property of Getaways Greece{" "}
          {new Date().getFullYear()}
        </CopyRight>
      </ContentContainer>
      <InfoButton onClick={openDialog}>
        <i className="fa-solid fa-info"></i>
      </InfoButton>
      <StyledDialog ref={dialogRef}>
        <DialogHeader>
          <h3>
            <u>Software Details</u>
          </h3>
          <button onClick={closeDialog}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </DialogHeader>
        <DialogContent>
          <div>
            <strong>Serial Number:</strong> GS-ERP-2024-001
          </div>
          <div>
            <strong>Registered to:</strong> Getaways Greece Private Company IKE
          </div>
          <div>
            <strong>VAT No:</strong> 800681442
          </div>
          <div>
            <strong>Registration Date:</strong> 01 July 2025
          </div>
          <div>
            <strong>Software:</strong> Getaways Suite ERP
          </div>
          <div>
            <strong>Author:</strong>{" "}
            <a href="https://app-ify.com" target="_blank" rel="noreferrer">
              APPIFY PC
            </a>
          </div>
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default LoginContent
